import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { getLimitedUserScope } from '../../utils/limitedUserAccess';
import { canUserAccessContabilidad } from '../../utils/contabilidadAccess';

const Sidebar = ({ mobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const [sessionUser, setSessionUser] = useState(() => authService.getCurrentUser());

  useEffect(() => {
    let cancelled = false;
    const refreshSessionUser = async () => {
      if (!authService.isAuthenticated()) {
        setSessionUser(null);
        return;
      }
      try {
        const merged = await authService.syncUserFromApi();
        if (!cancelled) setSessionUser(merged);
      } catch {
        if (!cancelled) setSessionUser(authService.getCurrentUser());
      }
    };
    refreshSessionUser();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const user = sessionUser;
  const isLimitedUser = user?.role === 'limitado';
  const isAccountant = user?.role === 'contador';
  const moduleAccess = user?.module_access || {};
  const limitedScope = getLimitedUserScope(user);
  const {
    machineryEnabled,
    produccionEnabled,
    produccionConcesionariaEnabled,
    concesionariaVentaOperacionesEnabled,
    produccionConcesionariaPlaneamientoEnabled,
    concesionariaOperacionesAlmacenEnabled,
    concesionariaRecetasEnabled,
    produccionLineasEnabled,
    restaurantesEnabled,
    activosEnabled,
    ventasEnabled,
    ventasCashLiquidationEnabled,
    servicesClassificationEnabled,
    productsProjectEnabled,
    kardexAuditModuleEnabled,
    comprasPurchaseSitesEnabled,
    loaded,
  } = useModuleSettings();

  const canSeeMachinery = !isAccountant && machineryEnabled && (!isLimitedUser || moduleAccess.machinery);
  const canSeeActivos = !isAccountant && activosEnabled && !isLimitedUser;
  const canSeeVentas = !isAccountant && ventasEnabled && (!isLimitedUser || limitedScope.showVentas);
  const canSeeKardexAudit = !isAccountant && kardexAuditModuleEnabled && limitedScope.showAlmacenes;
  const canSeeContabilidad = canUserAccessContabilidad(user);
  const canSeeProduccionGeneral =
    !isAccountant &&
    produccionEnabled &&
    (!isLimitedUser || moduleAccess.produccion || moduleAccess.produccion_concesionaria || moduleAccess.restaurantes);
  const canSeeProduccionConcesionariaMenu =
    !isAccountant &&
    (produccionConcesionariaEnabled || concesionariaVentaOperacionesEnabled) &&
    (!isLimitedUser || moduleAccess.produccion_concesionaria);
  const canSeeRestaurantes =
    !isAccountant &&
    restaurantesEnabled &&
    (!isLimitedUser || moduleAccess.restaurantes);

  /** Módulo concesionaria completo en menú Configuración (tiempos, PT, tipo plato), no solo venta/despacho. */
  const concesionariaConfigModuloCompleto =
    produccionConcesionariaEnabled &&
    (!concesionariaVentaOperacionesEnabled ||
      produccionConcesionariaPlaneamientoEnabled ||
      concesionariaOperacionesAlmacenEnabled ||
      concesionariaRecetasEnabled);

  const productClassificationSubmenu = [
    { name: 'Inicio', path: '/classification' },
    { name: 'Productos — Categorías', path: '/classification/categories' },
    { name: 'Productos — Subcategorías', path: '/classification/subcategories' },
    { name: 'Productos — Familias', path: '/classification/families' },
    { name: 'Productos — Vista jerárquica', path: '/classification/hierarchy' },
  ];

  const serviceClassificationSubmenu = servicesClassificationEnabled
    ? [
        { name: 'Servicios — Categorías', path: '/classification/services/categories' },
        { name: 'Servicios — Subcategorías', path: '/classification/services/subcategories' },
        { name: 'Servicios — Familias', path: '/classification/services/families' },
        { name: 'Servicios — Vista jerárquica', path: '/classification/services/hierarchy' },
      ]
    : [];

  const recetasClasificacionSubmenu = [
    { name: 'Categorías', path: '/produccion/clasificacion-recetas/categorias', icon: '📁', comingSoon: false },
    { name: 'Subcategorías', path: '/produccion/clasificacion-recetas/subcategorias', icon: '📂', comingSoon: false },
    { name: 'Familias', path: '/produccion/clasificacion-recetas/familias', icon: '📋', comingSoon: false },
    { name: 'Vista jerárquica', path: '/produccion/clasificacion-recetas/jerarquia', icon: '🌳', comingSoon: false },
  ];
  // Expandir automáticamente el menú de Gestión, Compras o Almacenes si estamos en esas rutas
  const [expandedMenus, setExpandedMenus] = useState(() => {
    const menus = new Set();
    if (location.pathname.startsWith('/classification') || 
        location.pathname.startsWith('/entities') || 
        location.pathname.startsWith('/products') || 
        location.pathname.startsWith('/services') || 
        location.pathname.startsWith('/sunat-mappings') || 
        location.pathname.startsWith('/configuration')) {
      menus.add('Gestión');
      if (location.pathname.startsWith('/classification')) {
        menus.add('Clasificación');
      }
      if (location.pathname.startsWith('/configuration')) {
        menus.add('Configuración');
      }
    }
    if (location.pathname.startsWith('/requirements') ||
        location.pathname.startsWith('/purchase-orders') || 
        location.pathname.startsWith('/service-orders') || 
        location.pathname.startsWith('/documents') ||
        location.pathname.startsWith('/invoices') ||
        location.pathname.startsWith('/guides') ||
        location.pathname.startsWith('/suppliers') ||
        location.pathname.startsWith('/compras/reports') ||
        location.pathname.startsWith('/continuous-services') ||
        location.pathname.startsWith('/supply-continuous-documents')) {
      menus.add('Compras');
      if (location.pathname.startsWith('/compras/reports')) {
        menus.add('compras-reportes');
      }
    }
    if (location.pathname.startsWith('/ventas')) {
      menus.add('Ventas');
      if (
        location.pathname.startsWith('/ventas/clients') ||
        location.pathname.startsWith('/ventas/price-list') ||
        location.pathname.startsWith('/ventas/pos-project-config') ||
        location.pathname.startsWith('/ventas/pos-series-config')
      ) {
        menus.add('ventas-configuracion');
      }
      if (location.pathname.startsWith('/ventas/reports')) {
        menus.add('ventas-reportes');
      }
    }
    if (location.pathname.startsWith('/contabilidad')) {
      menus.add('Contabilidad');
    }
    if (location.pathname.startsWith('/warehouses') || location.pathname.startsWith('/reports')) {
      menus.add('Almacenes');
      if (location.pathname.startsWith('/reports')) {
        menus.add('Reportes');
      }
    }
    if (location.pathname.startsWith('/audit')) {
      menus.add('Auditoría');
    }
    if (location.pathname.startsWith('/machinery')) {
      menus.add('Maquinarias');
      if (
        location.pathname.startsWith('/machinery/machines') ||
        location.pathname.startsWith('/machinery/vehicles') ||
        location.pathname.startsWith('/machinery/categories') ||
        location.pathname.startsWith('/machinery/conductors') ||
        location.pathname.startsWith('/machinery/events/config')
      ) {
        menus.add('machinery-configuracion');
      }
    }
    if (location.pathname.startsWith('/activos')) {
      menus.add('Activos');
      if (location.pathname.startsWith('/activos/configuracion')) {
        menus.add('activos-configuracion');
        if (location.pathname.startsWith('/activos/configuracion/clasificacion')) {
          menus.add('activos-clasificacion');
        }
      }
    }
    if (location.pathname.startsWith('/produccion') && !location.pathname.startsWith('/produccion-concesionaria')) {
      menus.add('Producción');
      if (location.pathname.startsWith('/produccion/clasificacion-recetas')) {
        menus.add('produccion-recetas');
        menus.add('produccion-clasificacion');
        if (!/\/clasificacion-recetas\/recetas(\/|$)/.test(location.pathname)) {
          menus.add('Clasificación de recetas');
        }
      }
      if (location.pathname.startsWith('/produccion/guias')) {
        menus.add('produccion-guias');
      }
      if (location.pathname.startsWith('/produccion/reportes')) {
        menus.add('produccion-reportes');
      }
    }
    if (location.pathname.startsWith('/produccion-concesionaria')) {
      menus.add('Producción concesionaria');
    }
    if (location.pathname.startsWith('/restaurantes')) {
      menus.add('Restaurantes');
    }
    if (location.pathname.startsWith('/restaurantes/operaciones')) {
      menus.add('restaurantes-operaciones');
    }
    if (location.pathname.startsWith('/produccion-concesionaria/configuraciones')) {
      menus.add('produccion-configuracion');
    }
    if (location.pathname.startsWith('/produccion-concesionaria/planeamiento')) {
      menus.add('produccion-planeamiento');
    }
    if (location.pathname.startsWith('/produccion-concesionaria/operaciones')) {
      menus.add('produccion-operaciones');
    }
    if (
      location.pathname.startsWith('/restaurantes/clasificacion-recetas') &&
      !/\/clasificacion-recetas\/recetas(\/|$)/.test(location.pathname)
    ) {
      menus.add('Restaurantes - Clasificación de recetas');
    }
    if (location.pathname.startsWith('/administration')) {
      menus.add('Administración');
      if (location.pathname.includes('traceability') ||
          location.pathname.includes('document-traceability') ||
          location.pathname.includes('purchase-order-traceability') ||
          location.pathname.includes('service-order-traceability') ||
          location.pathname.includes('entry-guide-traceability')) {
        menus.add('Trazabilidad');
      }
      if (location.pathname.startsWith('/administration/reports')) {
        menus.add('Reportes');
      }
    }
    if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) {
      menus.add('Administrador');
    }
    return menus;
  });

  // Actualizar el menú expandido cuando cambie la ruta
  useEffect(() => {
    const menus = new Set();
    if (location.pathname.startsWith('/classification') || 
        location.pathname.startsWith('/entities') || 
        location.pathname.startsWith('/products') || 
        location.pathname.startsWith('/services') || 
        location.pathname.startsWith('/sunat-mappings') || 
        location.pathname.startsWith('/configuration')) {
      menus.add('Gestión');
      if (location.pathname.startsWith('/classification')) {
        menus.add('Clasificación');
      }
      if (location.pathname.startsWith('/configuration')) {
        menus.add('Configuración');
      }
    }
    if (location.pathname.startsWith('/requirements') ||
        location.pathname.startsWith('/purchase-orders') || 
        location.pathname.startsWith('/service-orders') || 
        location.pathname.startsWith('/documents') ||
        location.pathname.startsWith('/invoices') ||
        location.pathname.startsWith('/guides') ||
        location.pathname.startsWith('/suppliers') ||
        location.pathname.startsWith('/compras/reports') ||
        location.pathname.startsWith('/continuous-services') ||
        location.pathname.startsWith('/supply-continuous-documents')) {
      menus.add('Compras');
      if (location.pathname.startsWith('/compras/reports')) {
        menus.add('compras-reportes');
      }
    }
    if (location.pathname.startsWith('/ventas')) {
      menus.add('Ventas');
      if (
        location.pathname.startsWith('/ventas/clients') ||
        location.pathname.startsWith('/ventas/price-list') ||
        location.pathname.startsWith('/ventas/pos-project-config') ||
        location.pathname.startsWith('/ventas/pos-series-config')
      ) {
        menus.add('ventas-configuracion');
      }
      if (location.pathname.startsWith('/ventas/reports')) {
        menus.add('ventas-reportes');
      }
    }
    if (location.pathname.startsWith('/contabilidad')) {
      menus.add('Contabilidad');
    }
    if (location.pathname.startsWith('/warehouses') || location.pathname.startsWith('/reports')) {
      menus.add('Almacenes');
      if (location.pathname.startsWith('/reports')) {
        menus.add('Reportes');
      }
    }
    if (location.pathname.startsWith('/audit')) {
      menus.add('Auditoría');
    }
    if (location.pathname.startsWith('/machinery')) {
      menus.add('Maquinarias');
      if (
        location.pathname.startsWith('/machinery/machines') ||
        location.pathname.startsWith('/machinery/vehicles') ||
        location.pathname.startsWith('/machinery/categories') ||
        location.pathname.startsWith('/machinery/conductors') ||
        location.pathname.startsWith('/machinery/events/config')
      ) {
        menus.add('machinery-configuracion');
      }
    }
    if (location.pathname.startsWith('/activos')) {
      menus.add('Activos');
      if (location.pathname.startsWith('/activos/configuracion')) {
        menus.add('activos-configuracion');
        if (location.pathname.startsWith('/activos/configuracion/clasificacion')) {
          menus.add('activos-clasificacion');
        }
      }
    }
    if (location.pathname.startsWith('/produccion') && !location.pathname.startsWith('/produccion-concesionaria')) {
      menus.add('Producción');
      if (location.pathname.startsWith('/produccion/clasificacion-recetas')) {
        menus.add('produccion-recetas');
        menus.add('produccion-clasificacion');
        if (!/\/clasificacion-recetas\/recetas(\/|$)/.test(location.pathname)) {
          menus.add('Clasificación de recetas');
        }
      }
      if (location.pathname.startsWith('/produccion/guias')) {
        menus.add('produccion-guias');
      }
      if (location.pathname.startsWith('/produccion/reportes')) {
        menus.add('produccion-reportes');
      }
    }
    if (location.pathname.startsWith('/produccion-concesionaria')) {
      menus.add('Producción concesionaria');
    }
    if (location.pathname.startsWith('/restaurantes')) {
      menus.add('Restaurantes');
    }
    if (location.pathname.startsWith('/restaurantes/operaciones')) {
      menus.add('restaurantes-operaciones');
    }
    if (location.pathname.startsWith('/produccion-concesionaria/configuraciones')) {
      menus.add('produccion-configuracion');
    }
    if (location.pathname.startsWith('/produccion-concesionaria/planeamiento')) {
      menus.add('produccion-planeamiento');
    }
    if (location.pathname.startsWith('/produccion-concesionaria/operaciones')) {
      menus.add('produccion-operaciones');
    }
    if (
      location.pathname.startsWith('/restaurantes/clasificacion-recetas') &&
      !/\/clasificacion-recetas\/recetas(\/|$)/.test(location.pathname)
    ) {
      menus.add('Restaurantes - Clasificación de recetas');
    }
    if (location.pathname.startsWith('/administration')) {
      menus.add('Administración');
      if (location.pathname.includes('traceability') ||
          location.pathname.includes('document-traceability') ||
          location.pathname.includes('purchase-order-traceability') ||
          location.pathname.includes('service-order-traceability') ||
          location.pathname.includes('entry-guide-traceability')) {
        menus.add('Trazabilidad');
      }
      if (location.pathname.startsWith('/administration/reports')) {
        menus.add('Reportes');
      }
    }
    if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) {
      menus.add('Administrador');
    }
    setExpandedMenus(menus);
  }, [location.pathname]);

  const menuItems = [
    ...(limitedScope.showDashboard !== false ? [{
      name: 'Dashboard',
      path: '/dashboard',
      icon: '📊',
    }] : []),
    ...(limitedScope.showGestion ? [{
      name: 'Gestión',
      path: '/classification',
      icon: '📋',
      submenu: [
        { name: 'Clasificación', path: '/classification', icon: '📁', submenu: [
          ...productClassificationSubmenu,
          ...serviceClassificationSubmenu,
        ]},
        { name: 'Entidades', path: '/entities', icon: '🏷️' },
        { name: 'Productos', path: '/products', icon: '📦' },
        { name: 'Servicios', path: '/services', icon: '🔧' },
        { name: 'Relación Productos SUNAT', path: '/sunat-mappings', icon: '🔗' },
        ...(!isLimitedUser
          ? [
              {
                name: 'Configuración',
                path: '/configuration',
                icon: '⚙️',
                submenu: [
                  { name: 'Unidades de Medida', path: '/configuration/units' },
                  { name: 'Conversiones', path: '/configuration/conversions' },
                  { name: 'Atributos', path: '/configuration/attributes' },
                  { name: 'Almacenes', path: '/configuration/warehouses' },
                  { name: 'Proyectos', path: '/configuration/projects' },
                  { name: 'Áreas', path: '/configuration/areas' },
                  { name: 'Hitos', path: '/configuration/milestones' },
                  { name: 'Series autonumeradas', path: '/configuration/document-number-series' },
                  { name: 'Puntos de Venta', path: '/configuration/point-of-sales' },
                  ...(comprasPurchaseSitesEnabled
                    ? [{ name: 'Sedes de compra', path: '/configuration/purchase-sites' }]
                    : []),
                ],
              },
            ]
          : []),
      ],
    }] : []),
    ...(limitedScope.showCompras ? [{
      name: 'Compras',
      path: '/purchase-orders',
      icon: '🛒',
      submenu: [
        { name: 'Proveedores', path: '/suppliers', icon: '👥', comingSoon: false },
        { name: 'Requerimientos', path: '/requirements', icon: '📋', comingSoon: false },
        { name: 'Órdenes de Compra', path: '/purchase-orders', icon: '🛒', comingSoon: false },
        { name: 'Órdenes de Servicio', path: '/service-orders', icon: '📋', comingSoon: false },
        { name: 'Servicios Continuos', path: '/continuous-services', icon: '🔄', comingSoon: false },
        { name: 'Documentos', path: '/documents', icon: '🧾', comingSoon: false },
        ...(comprasPurchaseSitesEnabled && !isLimitedUser
          ? [{ name: 'Asignar sede (histórico)', path: '/compras/purchase-site-assignment', icon: '🏬', comingSoon: false }]
          : []),
        ...(isLimitedUser && !limitedScope.showGestion
          ? [{ name: 'Relación Productos SUNAT', path: '/sunat-mappings', icon: '🔗', comingSoon: false }]
          : []),
        {
          name: 'Reportes',
          path: '/compras/reports/document-register',
          icon: '📈',
          expandKey: 'compras-reportes',
          submenu: [
            { name: 'Registro de documentos', path: '/compras/reports/document-register', icon: '🧾', comingSoon: false },
            { name: 'Dashboard de precios', path: '/compras/reports/price-tracking', icon: '📊', comingSoon: false },
            { name: 'Alertas de precio', path: '/compras/reports/price-tracking/alerts', icon: '⚠️', comingSoon: false },
            { name: 'Proveedores (compras)', path: '/compras/reports/price-tracking/suppliers', icon: '🏢', comingSoon: false },
            { name: 'Precios por producto', path: '/compras/reports/price-tracking/products', icon: '📦', comingSoon: false },
            { name: 'Historial de precios', path: '/compras/reports/price-tracking/history', icon: '📋', comingSoon: false },
          ],
        },
      ],
    }] : []),
    ...(canSeeVentas ? [{
      name: 'Ventas',
      path: '/ventas/clients',
      icon: '💰',
      submenu: [
        {
          name: 'Configuración',
          path: '/ventas/clients',
          icon: '⚙️',
          expandKey: 'ventas-configuracion',
          submenu: [
            { name: 'Clientes', path: '/ventas/clients', icon: '👥', comingSoon: false },
            { name: 'Lista de precios', path: '/ventas/price-list', icon: '💲', comingSoon: false },
            ...(productsProjectEnabled
              ? [{ name: 'Punto de venta a proyecto', path: '/ventas/pos-project-config', icon: '🔗', comingSoon: false }]
              : []),
            { name: 'Serie por punto de venta', path: '/ventas/pos-series-config', icon: '🔢', comingSoon: false },
            { name: 'Almacén por punto de venta', path: '/ventas/pos-warehouse-config', icon: '🏬', comingSoon: false },
          ],
        },
        { name: 'Cotización', path: '/ventas/quotes', icon: '📝', comingSoon: false },
        { name: 'Orden de servicio (venta)', path: '/ventas/service-orders', icon: '🧾', comingSoon: false },
        { name: 'Servicios', path: '/ventas/continuous-services', icon: '🔄', comingSoon: false },
        { name: 'Ventas', path: '/ventas/orders', icon: '🛍️', comingSoon: false },
        { name: 'Importar masiva', path: '/ventas/orders/bulk-import', icon: '📥', comingSoon: false },
        ...(ventasCashLiquidationEnabled
          ? [{ name: 'Liquidación de caja', path: '/ventas/caja', icon: '💵', comingSoon: false }]
          : []),
        {
          name: 'Reportes',
          path: '/ventas/reports',
          icon: '📈',
          expandKey: 'ventas-reportes',
          submenu: [
            { name: 'Dashboard de márgenes', path: '/ventas/reports', icon: '📊', comingSoon: false },
            { name: 'Dashboard de ventas', path: '/ventas/reports/ventas', icon: '💹', comingSoon: false },
            { name: 'Reporte de ventas', path: '/ventas/reports/registro', icon: '🧾', comingSoon: false },
            { name: 'Productos / márgenes', path: '/ventas/reports/productos', icon: '📦', comingSoon: false },
            ...(ventasCashLiquidationEnabled
              ? [{
                  name: 'Liquidaciones de caja',
                  path: '/ventas/reports/liquidaciones-caja',
                  icon: '💵',
                  comingSoon: false,
                }]
              : []),
          ],
        },
      ],
    }] : []),
    ...(limitedScope.showAlmacenes ? [{
      name: 'Almacenes',
      path: '/warehouses',
      icon: '🏭',
      submenu: [
        { name: 'Almacenes', path: '/warehouses', icon: '🏭', comingSoon: false },
          { name: 'Reportes', path: '/reports', icon: '📈', submenu: [
            { name: 'Dashboard de inventario', path: '/reports/inventory-dashboard', icon: '📊', comingSoon: false },
            { name: 'Entradas con cambio de costo', path: '/reports/entry-change-alerts', icon: '📈', comingSoon: false },
            { name: 'Kardex', path: '/reports/kardex', icon: '📋', comingSoon: false },
            { name: 'Inconsistencias de Kardex', path: '/reports/kardex-inconsistencies', icon: '⚠️', comingSoon: false },
            { name: 'Entradas y Salidas', path: '/reports/entries-exits', icon: '📊', comingSoon: false },
            { name: 'Matriz Entradas y Salidas', path: '/reports/entries-exits-matrix', icon: '📋', comingSoon: false },
            { name: 'Salidas por Entidad', path: '/reports/exits-by-supplier', icon: '📤', comingSoon: false },
          ]},
      ],
    }] : []),
    ...(canSeeKardexAudit ? [{
      name: 'Auditoría',
      path: '/audit',
      icon: '🔍',
      submenu: [
        { name: 'Eventos y alertas', path: '/audit/kardex', icon: '📋', comingSoon: false },
        { name: 'Reglas', path: '/audit/rules', icon: '⚙️', comingSoon: false },
        { name: 'Toma de inventario', path: '/audit/inventory-count', icon: '📦', comingSoon: false },
        { name: 'Mis tomas de inventario', path: '/audit/my-inventory-counts', icon: '✅', comingSoon: false },
        { name: 'Destinatarios', path: '/audit/recipients', icon: '👥', comingSoon: false },
      ],
    }] : []),
    ...(canSeeMachinery ? [{
      name: 'Maquinarias',
      path: '/machinery',
      icon: '🚜',
      submenu: [
        {
          name: 'Configuración',
          path: '/machinery/machines',
          icon: '⚙️',
          expandKey: 'machinery-configuracion',
          submenu: [
            { name: 'Máquinas', path: '/machinery/machines', icon: '🔧', comingSoon: false },
            { name: 'Vehículos', path: '/machinery/vehicles', icon: '🚗', comingSoon: false },
            { name: 'Categorías', path: '/machinery/categories', icon: '🗂️', comingSoon: false },
            { name: 'Conductores', path: '/machinery/conductors', icon: '👤', comingSoon: false },
            { name: 'Tipos de evento', path: '/machinery/events/config', icon: '⚙️', comingSoon: false },
          ],
        },
        { name: 'Eventos (Bitacora)', path: '/machinery/events', icon: '📋', comingSoon: false },
        { name: 'Alertas de mantenimiento', path: '/machinery/maintenance', icon: '🔔', comingSoon: false },
        { name: 'Historial de mantenimientos', path: '/machinery/maintenance/history', icon: '📜', comingSoon: false },
        { name: 'Reportes', path: '/machinery/reports', icon: '📊', comingSoon: false },
      ],
    }] : []),
    ...(canSeeActivos ? [{
      name: 'Activos',
      path: '/activos',
      icon: '📦',
      submenu: [
        { name: 'Listado', path: '/activos/listado', icon: '📋', comingSoon: false },
        { name: 'En uso por áreas', path: '/activos/en-uso', icon: '🏢', comingSoon: false },
        { name: 'Depreciación', path: '/activos/financiero/depreciacion', icon: '💰', comingSoon: false },
        {
          name: 'Configuración',
          path: '/activos/configuracion/areas',
          icon: '⚙️',
          expandKey: 'activos-configuracion',
          submenu: [
            {
              name: 'Clasificación',
              path: '/activos/configuracion/clasificacion/categorias',
              icon: '📁',
              expandKey: 'activos-clasificacion',
              submenu: [
                { name: 'Categorías', path: '/activos/configuracion/clasificacion/categorias', icon: '📁', comingSoon: false },
                { name: 'Subcategorías', path: '/activos/configuracion/clasificacion/subcategorias', icon: '📂', comingSoon: false },
                { name: 'Familias', path: '/activos/configuracion/clasificacion/familias', icon: '📋', comingSoon: false },
                { name: 'Vista jerárquica', path: '/activos/configuracion/clasificacion/jerarquia', icon: '🌳', comingSoon: false },
              ],
            },
            { name: 'Áreas', path: '/activos/configuracion/areas', icon: '🏢', comingSoon: false },
          ],
        },
      ],
    }] : []),
    ...(canSeeProduccionGeneral ? [{
      name: 'Producción',
      path: '/produccion',
      icon: '🏭',
      submenu: [
        {
          name: 'Recetas',
          path: '/produccion/clasificacion-recetas/recetas',
          icon: '📜',
          expandKey: 'produccion-recetas',
          submenu: [
            {
              name: 'Clasificación de recetas',
              path: '/produccion/clasificacion-recetas',
              icon: '📁',
              expandKey: 'produccion-clasificacion',
              submenu: recetasClasificacionSubmenu,
            },
            {
              name: 'Fichas de receta',
              path: '/produccion/clasificacion-recetas/recetas',
              icon: '📋',
              comingSoon: false,
            },
          ],
        },
        {
          name: 'Guías de producción',
          path: '/produccion/guias',
          icon: '📋',
          expandKey: 'produccion-guias',
          submenu: [
            {
              name: 'Producto terminado',
              path: '/produccion/guias/producto-terminado',
              icon: '✅',
              comingSoon: false,
            },
            {
              name: 'Producto en proceso',
              path: '/produccion/guias/producto-en-proceso',
              icon: '⚗️',
              comingSoon: false,
            },
          ],
        },
        ...(produccionLineasEnabled
          ? [{
              name: 'Líneas de producción',
              path: '/produccion/lineas',
              icon: '🔀',
              comingSoon: false,
            }]
          : []),
        {
          name: 'Reportes',
          path: '/produccion/reportes/dashboard',
          icon: '📊',
          expandKey: 'produccion-reportes',
          submenu: [
            {
              name: 'Dashboard',
              path: '/produccion/reportes/dashboard',
              icon: '📈',
              comingSoon: false,
            },
            {
              name: 'Uso de insumo en recetas',
              path: '/produccion/reportes/uso-insumo',
              icon: '🔍',
              comingSoon: false,
            },
          ],
        },
      ],
    }] : []),
    ...(canSeeProduccionConcesionariaMenu ? [{
      name: 'Producción concesionaria',
      path: '/produccion-concesionaria',
      icon: '🏢',
      submenu: [
        ...((concesionariaVentaOperacionesEnabled || produccionConcesionariaEnabled) ? [{
          name: 'Configuración',
          path: '/produccion-concesionaria/configuraciones',
          icon: '⚙️',
          expandKey: 'produccion-configuracion',
          submenu: [
            ...(concesionariaVentaOperacionesEnabled ? [
              { name: 'Área de despacho', path: '/produccion-concesionaria/configuraciones/area-despacho', icon: '📤', comingSoon: false },
              { name: 'Cuadro venta (plantillas)', path: '/produccion-concesionaria/configuraciones/cuadro-venta', icon: '📊', comingSoon: false },
            ] : []),
            ...(concesionariaConfigModuloCompleto ? [
              { name: 'Tiempos', path: '/produccion-concesionaria/configuraciones/tiempos', icon: '🕐', comingSoon: false },
              { name: 'Almacenes PT', path: '/produccion-concesionaria/configuraciones/almacenes-producto-terminado', icon: '🏭', comingSoon: false },
              { name: 'Tipo de plato', path: '/produccion-concesionaria/configuraciones/tipo-plato', icon: '🍽️', comingSoon: false },
              ...(!concesionariaVentaOperacionesEnabled ? [
                { name: 'Área de despacho', path: '/produccion-concesionaria/configuraciones/area-despacho', icon: '📤', comingSoon: false },
              ] : []),
            ] : []),
          ],
        }] : []),
        ...(produccionConcesionariaEnabled && (produccionConcesionariaPlaneamientoEnabled || !concesionariaVentaOperacionesEnabled) ? [{
          name: 'Planeamiento',
          path: '/produccion-concesionaria/planeamiento',
          icon: '📅',
          expandKey: 'produccion-planeamiento',
          submenu: [
            {
              name: 'Plantillas cíclicos',
              path: '/produccion-concesionaria/planeamiento/plantillas-ciclicos',
              icon: '🔄',
              comingSoon: false,
            },
            {
              name: 'Cíclicos',
              path: '/produccion-concesionaria/planeamiento/ciclicos',
              icon: '🔁',
              comingSoon: false,
            },
          ],
        }] : []),
        ...(concesionariaVentaOperacionesEnabled ? [{
          name: 'Venta producción',
          path: '/produccion-concesionaria/operaciones/produccion-dia',
          icon: '📋',
          comingSoon: false,
        }] : []),
        ...(concesionariaVentaOperacionesEnabled ? [{
          name: 'Dashboard venta producción',
          path: '/produccion-concesionaria/operaciones/venta-produccion-dashboard',
          icon: '📈',
          comingSoon: false,
        }] : []),
        ...(produccionConcesionariaEnabled && (concesionariaOperacionesAlmacenEnabled || !concesionariaVentaOperacionesEnabled) ? [{
          name: 'Operaciones almacén',
          path: '/produccion-concesionaria/operaciones',
          icon: '📤',
          expandKey: 'produccion-operaciones',
          submenu: [
            {
              name: 'Salidas a producción',
              path: '/produccion-concesionaria/operaciones/salidas-produccion',
              icon: '📦',
              comingSoon: false,
            },
            {
              name: 'Despacho',
              path: '/produccion-concesionaria/operaciones/despacho',
              icon: '📤',
              comingSoon: false,
            },
          ],
        }] : []),
      ],
    }] : []),
    ...(canSeeRestaurantes ? [{
      name: 'Restaurantes',
      path: '/restaurantes',
      icon: '🍴',
      submenu: [
        {
          name: 'Operaciones',
          path: '/restaurantes/operaciones',
          icon: '🧾',
          expandKey: 'restaurantes-operaciones',
          submenu: [
            {
              name: 'Pedidos',
              path: '/restaurantes/operaciones/pedidos',
              icon: '🧾',
              comingSoon: false,
            },
            {
              name: 'Mesas y barras',
              path: '/restaurantes/operaciones/acceso',
              icon: '🪑',
              comingSoon: false,
            },
            {
              name: 'Espacio de producción',
              path: '/restaurantes/operaciones/produccion',
              icon: '👨‍🍳',
              comingSoon: false,
            },
          ],
        },
        {
          name: 'Configuración',
          path: '/restaurantes/clasificacion-recetas',
          icon: '⚙️',
          submenu: [
            {
              name: 'Clasificación de recetas',
              path: '/restaurantes/clasificacion-recetas',
              icon: '📁',
              submenu: [
                { name: 'Categorías', path: '/restaurantes/clasificacion-recetas/categorias', icon: '📁', comingSoon: false },
                { name: 'Subcategorías', path: '/restaurantes/clasificacion-recetas/subcategorias', icon: '📂', comingSoon: false },
                { name: 'Familias', path: '/restaurantes/clasificacion-recetas/familias', icon: '📋', comingSoon: false },
                { name: 'Vista jerárquica', path: '/restaurantes/clasificacion-recetas/jerarquia', icon: '🌳', comingSoon: false },
              ],
            },
            { name: 'Recetas', path: '/restaurantes/clasificacion-recetas/recetas', icon: '📜', comingSoon: false },
            {
              name: 'Cocinas',
              path: '/restaurantes/configuraciones/cocinas',
              icon: '🍳',
              comingSoon: false,
            },
            {
              name: 'Mesas',
              path: '/restaurantes/configuraciones/mesas',
              icon: '🪑',
              comingSoon: false,
            },
          ],
        },
      ],
    }] : []),
    ...(limitedScope.showAdministracion ? [{
      name: 'Administración',
      path: '/administration',
      icon: '⚙️',
      submenu: [
        {
          name: 'Trazabilidad',
          path: '/administration',
          icon: '🔍',
          submenu: [
            { name: 'Trazabilidad de Documentos', path: '/administration/document-traceability', icon: '🔍', comingSoon: false },
            { name: 'Trazabilidad de Órdenes de Compra', path: '/administration/purchase-order-traceability', icon: '🛒', comingSoon: false },
            { name: 'Trazabilidad de Órdenes de Servicio', path: '/administration/service-order-traceability', icon: '📋', comingSoon: false },
            { name: 'Trazabilidad de Guías', path: '/administration/entry-guide-traceability', icon: '📦', comingSoon: false },
            { name: 'Trazabilidad de Requerimientos', path: '/administration/requirement-traceability', icon: '📋', comingSoon: false },
          ],
        },
        ...(limitedScope.showRegistroActividad && !limitedScope.showAdministracionCompleta
          ? [
              { name: 'Registro de actividad', path: '/administration/activity-logs', icon: '📋', comingSoon: false },
            ]
          : []),
        ...(limitedScope.showAdministracionCompleta !== false
          ? [
              {
                name: 'Reportes',
                path: '/administration/reports',
                icon: '📈',
                submenu: [
                  { name: 'Resultados', path: '/administration/reports/results', icon: '📊', comingSoon: false },
                ],
              },
              { name: 'Registro de actividad', path: '/administration/activity-logs', icon: '📋', comingSoon: false },
              {
                name: 'Informe de rendimiento por usuario',
                path: '/administration/user-performance-report',
                icon: '📊',
                comingSoon: false,
              },
              {
                name: 'Tipos de cambio',
                path: '/administration/exchange-rates',
                icon: '💱',
                comingSoon: false,
              },
            ]
          : []),
      ],
    }] : []),
    ...(canSeeContabilidad ? [{
      name: 'Contabilidad',
      path: '/contabilidad/reportes/compras-diferenciadas',
      icon: '📒',
      submenu: [
        {
          name: 'Compras diferenciadas',
          path: '/contabilidad/reportes/compras-diferenciadas',
          icon: '📊',
          comingSoon: false,
        },
      ],
    }] : []),
    // Solo mostrar Administrador si el usuario es administrador
    ...(user && user.role === 'administrador' ? [{
      name: 'Administrador',
      path: '/admin',
      icon: '👤',
      submenu: [
        { name: 'Habilitación de Módulos', path: '/admin/module-settings', icon: '🔌', comingSoon: false },
        { name: 'Configuraciones generales', path: '/admin/general-settings', icon: '⚙️', comingSoon: false },
        { name: 'Integración', path: '/admin/integration', icon: '🔗', comingSoon: false },
        { name: 'Usuarios', path: '/admin/users', icon: '👥', comingSoon: false },
      ],
    }] : []),
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  // Para ítems del submenú: evita que "Eventos (Bitacora)" se marque cuando se está en "Tipos de evento" (/machinery/events/config)
  const isSubItemActive = (subItemPath) => {
    if (subItemPath === '/machinery/events/config') {
      return location.pathname === '/machinery/events/config' || location.pathname.startsWith('/machinery/events/config/');
    }
    if (subItemPath === '/machinery/events') {
      return (location.pathname === '/machinery/events' || location.pathname.startsWith('/machinery/events/')) && !location.pathname.startsWith('/machinery/events/config');
    }
    // Alertas de mantenimiento: solo activo en la ruta exacta, no en /machinery/maintenance/history
    if (subItemPath === '/machinery/maintenance') {
      return location.pathname === '/machinery/maintenance';
    }
    if (subItemPath === '/machinery/maintenance/history') {
      return location.pathname === '/machinery/maintenance/history' || location.pathname.startsWith('/machinery/maintenance/history/');
    }
    if (subItemPath === '/machinery/reports') {
      return location.pathname === '/machinery/reports' || location.pathname.startsWith('/machinery/reports/');
    }
    if (subItemPath === '/compras/reports/document-register') {
      return location.pathname === '/compras/reports/document-register'
        || location.pathname.startsWith('/compras/reports/document-register/');
    }
    if (subItemPath === '/compras/reports/price-tracking') {
      return location.pathname === '/compras/reports/price-tracking';
    }
    if (subItemPath === '/compras/reports/price-tracking/alerts') {
      return location.pathname === '/compras/reports/price-tracking/alerts';
    }
    if (subItemPath === '/compras/reports/price-tracking/suppliers') {
      return location.pathname === '/compras/reports/price-tracking/suppliers';
    }
    if (subItemPath === '/compras/reports/price-tracking/products') {
      return location.pathname === '/compras/reports/price-tracking/products'
        || location.pathname.startsWith('/compras/reports/price-tracking/products/');
    }
    if (subItemPath === '/compras/reports/price-tracking/history') {
      return location.pathname === '/compras/reports/price-tracking/history';
    }
    if (subItemPath === '/ventas/reports') {
      return location.pathname === '/ventas/reports';
    }
    if (subItemPath === '/ventas/reports/ventas') {
      return location.pathname === '/ventas/reports/ventas' || location.pathname.startsWith('/ventas/reports/ventas/');
    }
    if (subItemPath === '/ventas/reports/registro') {
      return location.pathname === '/ventas/reports/registro' || location.pathname.startsWith('/ventas/reports/registro/');
    }
    if (subItemPath === '/ventas/reports/productos') {
      return location.pathname === '/ventas/reports/productos' || location.pathname.startsWith('/ventas/reports/productos/');
    }
    if (subItemPath === '/ventas/reports/liquidaciones-caja') {
      return location.pathname === '/ventas/reports/liquidaciones-caja';
    }
    if (subItemPath === '/ventas/caja') {
      return location.pathname === '/ventas/caja' || location.pathname.startsWith('/ventas/caja/');
    }
    if (subItemPath === '/ventas/clients') {
      return location.pathname === '/ventas/clients' || location.pathname.startsWith('/ventas/clients/');
    }
    if (subItemPath === '/ventas/quotes') {
      return location.pathname === '/ventas/quotes' || location.pathname.startsWith('/ventas/quotes/');
    }
    if (subItemPath === '/ventas/service-orders') {
      return location.pathname === '/ventas/service-orders' || location.pathname.startsWith('/ventas/service-orders/');
    }
    if (subItemPath === '/ventas/orders') {
      return (
        location.pathname === '/ventas/orders'
        || (location.pathname.startsWith('/ventas/orders/')
          && !location.pathname.startsWith('/ventas/orders/bulk-import'))
      );
    }
    if (subItemPath === '/ventas/orders/bulk-import') {
      return location.pathname.startsWith('/ventas/orders/bulk-import');
    }
    if (subItemPath === '/ventas/continuous-services') {
      return location.pathname === '/ventas/continuous-services' || location.pathname.startsWith('/ventas/continuous-services/');
    }
    if (subItemPath === '/reports/inventory-dashboard') {
      return location.pathname === '/reports/inventory-dashboard';
    }
    if (subItemPath === '/reports/entry-change-alerts') {
      return location.pathname === '/reports/entry-change-alerts';
    }
    // General (config producción): solo la pantalla de parámetros, no clasificación
    if (subItemPath === '/produccion-concesionaria/configuraciones') {
      return (
        location.pathname === '/produccion-concesionaria/configuraciones' ||
        location.pathname === '/produccion-concesionaria/configuraciones/' ||
        location.pathname.startsWith('/produccion-concesionaria/configuraciones/almacenes-producto-terminado') ||
        location.pathname.startsWith('/produccion-concesionaria/configuraciones/tiempos') ||
        location.pathname.startsWith('/produccion-concesionaria/configuraciones/tipo-plato') ||
        location.pathname.startsWith('/produccion-concesionaria/configuraciones/area-despacho') ||
        location.pathname.startsWith('/produccion-concesionaria/configuraciones/cuadro-venta')
      );
    }
    if (subItemPath === '/produccion/clasificacion-recetas') {
      return (
        location.pathname.startsWith('/produccion/clasificacion-recetas') &&
        !/\/clasificacion-recetas\/recetas(\/|$)/.test(location.pathname)
      );
    }
    // Matriz comparte prefijo con Entradas y Salidas (/reports/entries-exits)
    if (subItemPath === '/reports/entries-exits') {
      return location.pathname === '/reports/entries-exits';
    }
    if (subItemPath === '/reports/entries-exits-matrix') {
      return (
        location.pathname === '/reports/entries-exits-matrix' ||
        location.pathname.startsWith('/reports/entries-exits-matrix/')
      );
    }
    return location.pathname.startsWith(subItemPath);
  };

  const toggleSubmenu = (menuName) => {
    setExpandedMenus(prev => {
      const newMenus = new Set(prev);
      if (newMenus.has(menuName)) {
        newMenus.delete(menuName);
      } else {
        newMenus.add(menuName);
      }
      return newMenus;
    });
  };

  const isMenuExpanded = (menuName) => {
    return expandedMenus.has(menuName);
  };

  return (
    <div
      className={`w-64 bg-white border-r border-gray-200 h-screen fixed left-0 top-0 flex flex-col z-50 transition-transform duration-200 ease-in-out ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      } xl:translate-x-0`}
    >
      {/* Logo y manual */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-200 flex-shrink-0">
        <Link
          to="/manual"
          onClick={onMobileClose}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-800 hover:text-teal-950 mb-2"
        >
          <span aria-hidden>📖</span>
          Manual de usuario
        </Link>
        <div className="flex items-center justify-between gap-2">
          <Link to="/dashboard" className="flex items-center min-w-0" onClick={onMobileClose}>
            <h1 className="text-xl font-semibold text-teal-600 truncate">Contifine Logistic</h1>
          </Link>
          <button
            type="button"
            aria-label="Cerrar menú"
            onClick={onMobileClose}
            className="xl:hidden shrink-0 p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Menu Items - Scrollable (solo cuando ya se conocen los módulos activos) */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1 pb-4">
        {!loaded ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-gray-500">
            <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mb-3" aria-hidden />
            <p className="text-sm">Cargando menú...</p>
          </div>
        ) : (
        <>
        {menuItems.map((item) => (
          <div key={item.path}>
            {item.comingSoon ? (
              <div className="px-4 py-3 text-sm text-gray-400 cursor-not-allowed flex items-center gap-3 rounded-md">
                <span className="text-lg">{item.icon}</span>
                <span className="flex-1">{item.name}</span>
                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">Próximamente</span>
              </div>
            ) : item.submenu ? (
              <div>
                <button
                  onClick={() => toggleSubmenu(item.name)}
                  className={`w-full px-4 py-3 text-sm font-medium rounded-md transition-colors flex items-center justify-between ${
                    isActive(item.path)
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform ${
                      isMenuExpanded(item.name) ? 'rotate-90' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                {isMenuExpanded(item.name) && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.submenu.map((subItem) => (
                      <div key={subItem.path}>
                        {subItem.submenu ? (
                          // Submenú anidado (p. ej. Configuración dentro de Gestión o Producción)
                          <div>
                            <button
                              type="button"
                              onClick={() => toggleSubmenu(subItem.expandKey || subItem.name)}
                              className={`w-full px-4 py-2 text-sm rounded-md transition-colors flex items-center justify-between ${
                                location.pathname.startsWith(subItem.path)
                                  ? 'bg-teal-50 text-teal-700 font-medium'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {subItem.icon && <span className="text-base">{subItem.icon}</span>}
                                <span>{subItem.name}</span>
                              </div>
                              <svg
                                className={`w-3 h-3 transition-transform ${
                                  isMenuExpanded(subItem.expandKey || subItem.name) ? 'rotate-90' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {isMenuExpanded(subItem.expandKey || subItem.name) && (
                              <div className="ml-4 mt-1 space-y-1">
                                {subItem.submenu.map((nestedItem) => (
                                  <div key={nestedItem.path}>
                                    {nestedItem.submenu ? (
                                      <div>
                                        <button
                                          type="button"
                                          onClick={() => toggleSubmenu(nestedItem.name)}
                                          className={`w-full px-4 py-2 text-sm rounded-md transition-colors flex items-center justify-between ${
                                            location.pathname.startsWith(nestedItem.path) &&
                                            !(
                                              nestedItem.name === 'Clasificación de recetas' &&
                                              /\/clasificacion-recetas\/recetas(\/|$)/.test(location.pathname)
                                            )
                                              ? 'bg-teal-50 text-teal-700 font-medium'
                                              : 'text-gray-600 hover:bg-gray-50'
                                          }`}
                                        >
                                          <div className="flex items-center gap-2">
                                            {nestedItem.icon && <span className="text-base">{nestedItem.icon}</span>}
                                            <span>{nestedItem.name}</span>
                                          </div>
                                          <svg
                                            className={`w-3 h-3 transition-transform ${
                                              isMenuExpanded(nestedItem.name) ? 'rotate-90' : ''
                                            }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                        </button>
                                        {isMenuExpanded(nestedItem.name) && (
                                          <div className="ml-4 mt-1 space-y-1">
                                            {nestedItem.submenu.map((deepItem) => (
                                              <Link
                                                key={deepItem.path}
                                                to={deepItem.path}
                                                className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${
                                                  isSubItemActive(deepItem.path)
                                                    ? 'bg-teal-50 text-teal-700 font-medium'
                                                    : 'text-gray-600 hover:bg-gray-50'
                                                }`}
                                              >
                                                {deepItem.icon && <span className="text-base">{deepItem.icon}</span>}
                                                <span>{deepItem.name}</span>
                                              </Link>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ) : nestedItem.comingSoon ? (
                                      <div className="px-4 py-2 text-sm text-gray-400 cursor-not-allowed flex items-center gap-2 rounded-md">
                                        {nestedItem.icon && <span className="text-base">{nestedItem.icon}</span>}
                                        <span className="flex-1">{nestedItem.name}</span>
                                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">Próximamente</span>
                                      </div>
                                    ) : (
                                      <Link
                                        to={nestedItem.path}
                                        className={`block px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${
                                          isSubItemActive(nestedItem.path)
                                            ? 'bg-teal-50 text-teal-700 font-medium'
                                            : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                      >
                                        {nestedItem.icon && <span className="text-base">{nestedItem.icon}</span>}
                                        <span>{nestedItem.name}</span>
                                      </Link>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : subItem.comingSoon ? (
                          // Item con "Próximamente"
                          <div className="px-4 py-2 text-sm text-gray-400 cursor-not-allowed flex items-center gap-2 rounded-md">
                            {subItem.icon && <span className="text-base">{subItem.icon}</span>}
                            <span className="flex-1">{subItem.name}</span>
                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">Próximamente</span>
                          </div>
                        ) : (
                          // Item simple del submenú
                          <Link
                            to={subItem.path}
                            className={`block px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${
                              isSubItemActive(subItem.path)
                                ? 'bg-teal-50 text-teal-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {subItem.icon && <span className="text-base">{subItem.icon}</span>}
                            <span>{subItem.name}</span>
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                to={item.path}
                className={`px-4 py-3 text-sm font-medium rounded-md transition-colors flex items-center gap-3 ${
                  isActive(item.path)
                    ? 'bg-teal-50 text-teal-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span>{item.name}</span>
              </Link>
            )}
          </div>
        ))}
        </>
        )}
      </nav>

      {/* User Section - Fixed at bottom */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
        <Link
          to="/profile"
          className="block mb-3 p-2 rounded-md hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-sm font-medium text-gray-700 truncate" title={user?.name || 'Usuario'}>
                {user?.name || 'Usuario'}
              </p>
              <p className="text-xs text-gray-500 truncate" title={user?.email || ''}>
                {user?.email || ''}
              </p>
            </div>
            <span className="text-gray-400 text-xs whitespace-nowrap flex-shrink-0">Ver perfil →</span>
          </div>
        </Link>
        <button
          onClick={() => {
            authService.logout();
            window.location.href = '/login';
          }}
          className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-medium transition-colors"
        >
          Cerrar Sesión
        </button>
      </div>
    </div>
  );
};

export default Sidebar;

